import { useLocation, useNavigate } from 'react-router';
import { useSession } from '@/auth/session-context';
import type { Environment } from '@/auth/token-store';
import { notify } from '@/components/ui/toast-store';

const ID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Switch tenant and/or environment. A switch issues a new token and drops
 * every cached query; detail pages hold ids from the previous scope, so they
 * fall back to their list.
 */
export function useContextSwitch() {
  const { switchContext, switching } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  async function switchTo(target: { tenantId?: string; environment: Environment }) {
    try {
      const session = await switchContext(target);
      const segments = location.pathname.split('/').filter(Boolean);
      const idIndex = segments.findIndex((segment) => ID_SEGMENT.test(segment));
      if (target.tenantId || idIndex >= 0) {
        const keep = idIndex >= 0 ? segments.slice(0, idIndex) : segments;
        void navigate(target.tenantId ? '/' : `/${keep.join('/')}`, { replace: true });
      }
      notify.success(
        session.environment === 'live' ? 'Viewing live data' : 'Viewing test data',
        `${session.tenant.name} · ${session.environment}`,
      );
    } catch (error) {
      notify.error(error, 'Could not switch');
    }
  }

  return { switchTo, switching };
}
